import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import {
  AutocompleteResponse,
  BuiltinControlAutocompleteRequest,
  ControlChange,
  ControlParameters,
  ControlState,
  EntityFeatureGetResponse,
  EntityFeaturePatchConflictResponse,
  EntityFeaturePatchRequest,
} from '../../../api/model/models';
import { CfContextService } from '../../../utils';
import { CplaceControlService } from './cplace-control.service';

/**
 * A control change handler is a function that takes a mock {@link ControlState}, a {@link ControlChange}
 * generated when submitting a control, and optionally some mock data to produce a new {@link ControlParameters}
 * to be sent to the control. This essentially mocks the backend for when a control's data is submitted.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ControlChangeHandler<U extends ControlChange = any, V extends ControlParameters = any, T = any> = (
  controlParameters: V,
  controlChange: U,
  mockData?: T
) => V;

/**
 * The mock submit request mapping requires a mock {@link ControlState} and a corresponding
 * {@link ControlChangeHandler}. Additionally, mock data can be provided and will be passed to the
 * {@link ControlChangeHandler} when a control is submitted.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface MockSubmitRequestMapping<U extends ControlChange = any, V extends ControlParameters = any, T = any> {
  controlState: ControlState;
  controlChangeHandler: ControlChangeHandler<U, V, T>;
  mockData?: T;
}

/**
 * Mocks out the endpoints available in the {@link CplaceControlService}. In unit tests, a specific mock response
 * can be set using the <code>setMock</code> functions. For use in storybook, it useful to provide request mappings
 * through the constructor. See {@link MockCplaceControlService#constructor} for what kind of request mappings can
 * be set.
 */
export class MockCplaceControlService extends CplaceControlService {
  private static readonly NO_MOCK_RESPONSES_AVAILABLE = 'No mock responses available';

  private mockSubmitResponse?: ControlState[] | EntityFeaturePatchConflictResponse;
  private mockSubmitRequestMapping: Map<string, MockSubmitRequestMapping> = new Map<string, MockSubmitRequestMapping>();

  private mockGetResponse?: EntityFeatureGetResponse;
  private mockGetRequestMapping: Map<string, EntityFeatureGetResponse> = new Map<string, EntityFeatureGetResponse>();

  private mockSearchBuiltinControlAutocompleteResponse?: AutocompleteResponse;
  private mockSearchBuiltinControlAutocompleteRequestMapping: Map<string, AutocompleteResponse> = new Map<string, AutocompleteResponse>();

  /**
   * Creates a {@link MockCplaceControlService} instance with the supplied mock request mappings.
   *
   * @param httpClient The {@link HttpClient} that should be injected when initializing the service in an
   * Angular <code>Provider</code>.
   * @param cfContextService The {@link CfContextService} that should be injected when initializing the service in an
   * Angular <code>Provider</code>.
   * @param mockSubmitRequestMapping Mapping of feature name to {@link MockSubmitRequestMapping}.
   * @param mockGetRequestMapping Mapping of entity uid to {@link EntityFeatureGetResponse}.
   * @param mockSearchAutocompleteRequestMapping Mapping of feature name {@link AutocompleteResponse}.
   */
  constructor(
    httpClient: HttpClient,
    cfContextService: CfContextService,
    mockSubmitRequestMapping?: Map<string, MockSubmitRequestMapping>,
    mockGetRequestMapping?: Map<string, EntityFeatureGetResponse>,
    mockSearchAutocompleteRequestMapping?: Map<string, AutocompleteResponse>
  ) {
    super(httpClient, cfContextService);

    if (mockSubmitRequestMapping) {
      this.mockSubmitRequestMapping = mockSubmitRequestMapping;
    }

    if (mockGetRequestMapping) {
      this.mockGetRequestMapping = mockGetRequestMapping;
    }

    if (mockSearchAutocompleteRequestMapping) {
      this.mockSearchBuiltinControlAutocompleteRequestMapping = mockSearchAutocompleteRequestMapping;
    }
  }

  /**
   * Sets a specific mock submit response. {@link MockCplaceControlService#submit} will immediately return this.
   *
   * @param response Mock submit response.
   */
  public setMockSubmitResponse(response: ControlState[] | EntityFeaturePatchConflictResponse) {
    this.mockSubmitResponse = response;
  }

  /**
   * Sets a specific mock get response. {@link MockCplaceControlService#get} will immediately return this.
   *
   * @param response Mock get response.
   */
  public setMockGetResponse(response: EntityFeatureGetResponse) {
    this.mockGetResponse = response;
  }

  /**
   * Sets a specific mock search autocomplete response. {@link
   * MockCplaceControlService#searchBuiltinControlAutocompleteGet} will immediately return this.
   *
   * @param response Mock search autocomplete response.
   */
  public setMockSearchBuiltinControlAutocompleteResponse(response: AutocompleteResponse) {
    this.mockSearchBuiltinControlAutocompleteResponse = response;
  }

  /**
   * Returns a mock submit response based off the passed in request. {@link MockCplaceControlService#mockSubmitResponse}
   * will be returned if it is set, otherwise a response will be generated based off the control change and given
   * mock request mapping.
   *
   * @param entityFeaturePatchRequest An {@link EntityFeaturePatchRequest}.
   */
  public submit(entityFeaturePatchRequest: EntityFeaturePatchRequest): Observable<ControlState[] | EntityFeaturePatchConflictResponse> {
    if (this.mockSubmitResponse) {
      return of(this.mockSubmitResponse);
    }

    if (entityFeaturePatchRequest.changes.length < 1) {
      return throwError('No control change supplied');
    }

    const controlChange = entityFeaturePatchRequest.changes[0];
    const mapping = this.mockSubmitRequestMapping.get(controlChange.featureName);

    if (mapping) {
      return of([
        {
          ...mapping.controlState,
          controlParameters: mapping.controlChangeHandler(mapping.controlState.controlParameters, controlChange, mapping.mockData),
        },
      ]);
    }

    return throwError(MockCplaceControlService.NO_MOCK_RESPONSES_AVAILABLE);
  }

  /**
   * Returns a mock get response based off the passed in request. {@link MockCplaceControlService#mockGetResponse}
   * will be returned if it is set, otherwise a response will be returned based off the entity uid and its
   * corresponding {@link EntityFeatureGetResponse}.
   *
   * @param entityUid Entity uid to look up corresponding {@link EntityFeatureGetResponse} for.
   */
  public get(entityUid: string): Observable<EntityFeatureGetResponse> {
    if (this.mockGetResponse) {
      return of(this.mockGetResponse);
    }

    const response = this.mockGetRequestMapping.get(entityUid);

    if (response) {
      return of(response);
    }

    return throwError(MockCplaceControlService.NO_MOCK_RESPONSES_AVAILABLE);
  }

  /**
   * Returns a mock search autocomplete get response based off the passed in request. {@link
   * MockCplaceControlService#mockSearchBuiltinControlAutocompleteResponse} will be returned if it is set, otherwise a
   * response will be generated based off the request and corresponding request mapping. The corresponding response hits
   * will be filtered by request's query.
   *
   * @param request A {@link BuiltinControlAutocompleteRequest}. Only {@link BuiltinControlAutocompleteRequest#query} is
   * used in the mock.
   * @param featureName Feature name to look up corresponding {@link AutocompleteResponse} for.
   */
  public searchBuiltinControlAutocompleteGet(
    request: BuiltinControlAutocompleteRequest,
    featureName: string
  ): Observable<AutocompleteResponse> {
    if (this.mockSearchBuiltinControlAutocompleteResponse) {
      return this.filterSearchBuiltinControlAutocompleteResponse(this.mockSearchBuiltinControlAutocompleteResponse, request);
    }

    const response = this.mockSearchBuiltinControlAutocompleteRequestMapping.get(featureName);

    if (response) {
      return this.filterSearchBuiltinControlAutocompleteResponse(response, request);
    }

    return throwError(MockCplaceControlService.NO_MOCK_RESPONSES_AVAILABLE);
  }

  private filterSearchBuiltinControlAutocompleteResponse(
    response: AutocompleteResponse,
    request: BuiltinControlAutocompleteRequest
  ): Observable<AutocompleteResponse> {
    return of({
      hits: response.hits.filter((hit) => hit.label.toLowerCase().indexOf((request?.query ?? '').toLowerCase()) !== -1),
      totalHitCount: 500,
    });
  }
}
